// eslint-disable-next-line import/named
import { customElement, state } from '@lit/reactive-element/decorators.js';
import { DataBinding, getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding.js';
import {
  DataEntryPageControlView,
  PageControlChildTab
} from '../../../webmodule-common/other/ui/data-entry-screen-base.js';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker.js';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { errorList } from '../../../webmodule-common/other/error-list.js';
import { EventTemplate, EventVoidAsync, Snippet } from '../../../webmodule-common/other/ui/events.js';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant.js';
import { getApiFactory } from '../../api/api-injector.js';
import { getQuoteSuppliers, isMultiSupplier, QuoteSupplier } from '../../quotes/quote-service';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { LangFranchiseePaymentProfileDetailView } from '../../../localization/scripts/dealer-franchisee/franchisee/view/franchisee-payment-profile-detail-view-lang';
import { LangTermsConditionsDetailView } from '../../../localization/scripts/franchisee/view/terms-conditions-detail-view-lang.js';
import {
  TermsAndConditions
} from '../../api/dealer-api-interface-franchisee.js';
import { TermsAndConditionsApi } from '../../api/terms-and-conditions-api.js';
import { TermsConditionContainerManager } from '../data/terms-conditions-container-manager.js';

export interface TermsConditionsDetailViewOptions {
  termsConditionsManger: TermsConditionContainerManager;
}

@customElement('wm-termsconditionsdetailview')
export class TermsConditionsDetailView extends PageControlChildTab {
  protected supplierApi = getApiFactory().supplier();
  @state()
  protected suppliers: QuoteSupplier[] = [];

  termsConditionsManager: TermsConditionContainerManager;
  termsConditionsApi: TermsAndConditionsApi = getApiFactory().termsAndConditions();
  internalId: string;
  dataBinding: DataBinding;
  dataTracker: DataTracker;
  eventRefreshPage: EventVoidAsync | null = null;

  constructor(owner: DataEntryPageControlView, options: TermsConditionsDetailViewOptions) {
    super(owner);
    this.internalId = getInternalId();

    this.termsConditionsManager = options.termsConditionsManger;

    this.dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input, internalId) => `terms-conditions-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(this.dataBinding);

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.termsConditions),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('name');
    addField('terms', FieldType.string, false);
    addField('isDefault', FieldType.boolean, false);
    addField('supplierId');
  }

  protected get termsConditions(): TermsAndConditions {
    return this.termsConditionsManager.termsConditions;
  }

  async afterConstruction(): Promise<void> {
    await super.afterConstruction();
  }

  async onEnter(): Promise<void> {
    await this.loadOrRefresh();
  }

  public allowDeletePage(): boolean {
    return false;
  }

  public async loadOrRefresh(): Promise<void> {
    await this.termsConditionsManager.needsTaC();
    this.suppliers = await getQuoteSuppliers();
    if (!isMultiSupplier() && this.isNew()) {
      this.termsConditions.supplierId = this.suppliers.length == 1 ? this.suppliers[0].supplierId : emptyGuid;
      this.termsConditionsManager.resetBackup();
    }

    this.requestUpdate();
  }

  protected isNew(): boolean {
    return this.termsConditions.id == emptyGuid;
  }

  public async prepareForSave() {
    this.dataTracker.applyChangeToValue();
  }

  public getValidationErrors(): string[] {
    const validator = errorList();
    validator.assert(
      !isEmptyOrSpace(this.termsConditions?.name),
      LangTermsConditionsDetailView.provideTermsConditionsName()
    );
    return validator.errors;
  }

  protected getCaption(): Snippet {
    return LangTermsConditionsDetailView.termsConditionsInfo();
  }

  protected bodyTemplate(): EventTemplate {
    const forms = new FormInputAssistant(this.dataTracker);
    forms.immediateBindingUpdate = true;

    if (isMultiSupplier()) {
      this.suppliers.unshift({ description: '', supplierId: emptyGuid, online: false });
    }

    const supplierConverter = (qs: QuoteSupplier) => {
      return {
        text: LangFranchiseePaymentProfileDetailView.supplierDescription({ description: qs.description }),
        value: qs.supplierId,
        disabled: false
      };
    };

    const isSupplierSelectorReadOnly = this.termsConditions.supplierId != emptyGuid && !this.isNew();
    const supplierSelectorControl = isMultiSupplier()
      ? forms.arraySelect('supplierId', this.suppliers, supplierConverter, {
        title: LangFranchiseePaymentProfileDetailView.supplierTitle(),
        readonly: isSupplierSelectorReadOnly,
      })
      : forms.textHidden('supplierId');

    return html`
      <form class="frm-terms-details form-one-col">
        <div class="row">
          <div>
            ${forms.textRequired('name', LangTermsConditionsDetailView.termsConditions(), 100)}
            ${supplierSelectorControl}
            ${forms.note('terms', LangTermsConditionsDetailView.terms(), 10000, undefined, undefined, 15)}
            ${forms.switch('isDefault', LangTermsConditionsDetailView.isDefault())}
          </div>
        </div>
      </form>
    `;
  }


}
